<template>
  <div class="min-h-screen bg-[#FFF7EF]">
    <div class="mx-auto flex min-h-screen max-w-7xl items-center justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
      <section class="w-full max-w-xl rounded-[32px] border border-gray-100 bg-white p-5 shadow-sm sm:p-8 lg:p-10">
        <h2 class="text-2xl font-semibold text-ink sm:text-[2rem]">Log in</h2>
        <p class="mt-2 text-sm leading-7 text-muted sm:text-base">{{ helperCopy }}</p>

        <div class="mt-6 grid grid-cols-2 gap-2 rounded-2xl bg-[#FFF4EA] p-1.5">
          <button
            type="button"
            class="auth-tab"
            :class="{ 'auth-tab-active': activeRole === 'user' }"
            @click="setRole('user')"
          >
            User
          </button>
          <button
            type="button"
            class="auth-tab"
            :class="{ 'auth-tab-active': activeRole === 'vendor' }"
            @click="setRole('vendor')"
          >
            Vendor
          </button>
        </div>

        <form class="mt-8 space-y-5" @submit.prevent="handleSubmit">
          <p v-if="error" class="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs font-semibold text-red-700">
            {{ error }}
          </p>

          <label class="field-block">
            <div class="field-shell" :class="fieldShellClass(focusedField === 'username', username)">
              <span class="field-floating-label">Email ID / Phone no*</span>
              <input
                v-model.trim="username"
                type="text"
                required
                class="field-control"
                @focus="focusedField = 'username'"
                @blur="focusedField = ''"
              >
            </div>
          </label>

          <label class="field-block">
            <div class="field-shell" :class="fieldShellClass(focusedField === 'password', password)">
              <span class="field-floating-label">Password*</span>
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                required
                class="field-control pr-14"
                @focus="focusedField = 'password'"
                @blur="focusedField = ''"
              >
              <button
                type="button"
                class="action-btn single-action"
                @click="showPassword = !showPassword"
                aria-label="Toggle password visibility"
              >
                <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
              </button>
            </div>
          </label>

          <div class="flex flex-wrap items-center justify-between gap-3 text-xs text-muted">
            <label class="inline-flex items-center gap-2">
              <input type="checkbox" class="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand">
              <span>Keep me signed in</span>
            </label>
            <RouterLink class="font-semibold text-brand" to="/forgot-password">Forgot password?</RouterLink>
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="w-full rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark"
          >
            {{ loading ? 'Signing in...' : submitLabel }}
          </button>
        </form>

        <div class="mt-8 text-center text-xs text-muted">
          New to Invi8?
          <RouterLink class="font-semibold text-brand" :to="registerLink">Create an account</RouterLink>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { setAuthSession, setAuthToken, setAuthUser } from '@/services/auth'

const router = useRouter()
const route = useRoute()

const username = ref('')
const password = ref('')
const loading = ref(false)
const showPassword = ref(false)
const error = ref('')
const focusedField = ref('')
const activeRole = ref(route.query.role === 'vendor' ? 'vendor' : 'user')

watch(
  () => route.query.role,
  (role) => {
    activeRole.value = role === 'vendor' ? 'vendor' : 'user'
  }
)

const helperCopy = computed(() =>
  activeRole.value === 'vendor'
    ? 'Use your vendor credentials to continue.'
    : 'Use your Invi8 account details to continue.'
)

const submitLabel = computed(() =>
  activeRole.value === 'vendor' ? 'Continue to dashboard' : 'Continue'
)

const registerLink = computed(() =>
  activeRole.value === 'vendor' ? '/vendor-signup?role=vendor' : '/register?role=user'
)

const fieldShellClass = (focused, value) => ({
  'field-shell-active': focused || Boolean(value)
})

const setRole = (role) => {
  router.replace({
    name: 'login',
    query: {
      ...route.query,
      role
    }
  })
}

const resolveUserId = (user) =>
  user?.s_id ||
  user?.id ||
  user?.user_id ||
  user?.vendor_id ||
  user?.vendorId ||
  ''

const normalizeUser = (data, fallbackName) => {
  const responseItems = Array.isArray(data?.data) ? data.data : []
  const primaryUser = responseItems[0] || null
  const responseData = data?.data && typeof data.data === 'object' ? data.data : null
  return (
    data?.user ||
    primaryUser ||
    data?.data?.user ||
    data?.data?.profile ||
    data?.profile ||
    responseData ||
    (fallbackName
      ? {
          name: fallbackName,
          username: fallbackName
        }
      : null)
  )
}

const handleSubmit = async () => {
  error.value = ''
  loading.value = true

  const formData = new FormData()
  formData.append('s_username', username.value)
  formData.append('s_password', password.value)

  try {
    const { data } =
      activeRole.value === 'vendor'
        ? await api.loginVendor(formData)
        : await api.login(formData)

    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Login failed')
    }

    const token = data?.token || data?.access_token || data?.data?.token || data?.data?.access_token
    const user = normalizeUser(data, username.value)
    const resolvedUserId = resolveUserId(user)

    if (token) {
      setAuthToken(token)
    }

    if (user) {
      setAuthUser({
        ...user,
        s_id: resolvedUserId || user?.s_id || ''
      })
    }

    setAuthSession({
      loggedIn: true,
      role: activeRole.value,
      s_id: resolvedUserId
    })

    const defaultRedirect = activeRole.value === 'vendor' ? '/dashboard' : '/'
    const redirectPath = route.query.redirect || defaultRedirect
    router.push(redirectPath)
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Login failed'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.auth-tab {
  border: 0;
  border-radius: 16px;
  background: transparent;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 700;
  color: #6b7280;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.auth-tab-active {
  background: #ffffff;
  color: #1f2937;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.field-block {
  display: block;
}

.field-shell {
  position: relative;
  min-height: 72px;
  border: 1px solid #d8dde5;
  border-radius: 18px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.field-shell-active {
  border-color: #ec2263;
  box-shadow: 0 0 0 4px rgba(236, 34, 99, 0.08);
}

.field-floating-label {
  position: absolute;
  left: 18px;
  top: 24px;
  font-size: 16px;
  color: #9ca3af;
  pointer-events: none;
  transition: transform 0.18s ease, top 0.18s ease, font-size 0.18s ease, color 0.18s ease;
}

.field-shell-active .field-floating-label {
  top: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.field-control {
  width: 100%;
  min-height: 72px;
  border: 0;
  border-radius: 18px;
  background: transparent;
  padding: 28px 18px 12px;
  font-size: 16px;
  color: #111827;
}

.field-control:focus {
  outline: none;
}

.single-action {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 999px;
  background: rgba(156, 163, 175, 0.12);
  color: #6b7280;
}
</style>
